import apiClient from '@/lib/api/apiClient'
import type {
  YardiResidentMatch,
  YardiResidentSyncResponse,
  YardiSyncContext,
  YardiTCodeValidationResponse,
} from '../types'

function unwrapObject<T>(raw: unknown): T {
  let value = raw
  for (let depth = 0; depth < 5; depth += 1) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) break
    const object = value as Record<string, unknown>
    const nested = object.data ?? object.result ?? object.payload
    if (!nested || typeof nested !== 'object') break
    value = nested
  }
  return value as T
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function hasSyncResponseShape(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) return false
  return ['matched', 'skipped', 'alreadySynced', 'already_synced', 'summary'].some((key) =>
    Object.prototype.hasOwnProperty.call(value, key),
  )
}

function normalizeSyncResponse(raw: unknown): YardiResidentSyncResponse {
  let value = raw
  for (let depth = 0; depth < 3 && !hasSyncResponseShape(value); depth += 1) {
    if (!isRecord(value)) break
    const nested = value.data ?? value.result ?? value.payload
    if (!isRecord(nested)) break
    value = nested
  }
  const object = hasSyncResponseShape(value) ? value : {}
  const alreadySynced = Array.isArray(object.alreadySynced)
    ? object.alreadySynced
    : Array.isArray(object.already_synced)
      ? object.already_synced
      : []
  return {
    ...object,
    matched: Array.isArray(object.matched) ? (object.matched as YardiResidentMatch[]) : [],
    skipped: Array.isArray(object.skipped) ? (object.skipped as YardiResidentMatch[]) : [],
    alreadySynced: alreadySynced as YardiResidentMatch[],
    summary: isRecord(object.summary) ? object.summary : {},
  }
}

export async function fetchYardiPropertyContext(propertyId: string | number): Promise<{
  companyId: string | number
  propertyCode: string
  propertyName: string
}> {
  const { data } = await apiClient.get<Record<string, unknown>>(`/api/property/${propertyId}`)
  const property = unwrapObject<Record<string, unknown>>(data)
  const config = (property.propertyConfig ?? property.property_config ?? property) as Record<string, unknown>
  return {
    companyId: (property.fk_company_id ?? property.company_id ?? '') as string | number,
    propertyCode: String(config.yardi_code ?? ''),
    propertyName: String(property.property_name ?? property.name ?? ''),
  }
}

export async function syncYardiResidents(context: YardiSyncContext): Promise<YardiResidentSyncResponse> {
  const { data } = await apiClient.post('/api/sync-yardi-residents', context)
  return normalizeSyncResponse(data)
}

export async function confirmYardiResidentMappings(
  context: YardiSyncContext,
  selectedMappings: YardiResidentMatch[],
): Promise<unknown> {
  const syncContext: Partial<YardiSyncContext> = { ...context }
  delete syncContext.resident_default_id
  const { data } = await apiClient.post('/api/confirm-yardi-resident-mappings', {
    ...syncContext,
    selected_mappings: selectedMappings,
  })
  return unwrapObject<unknown>(data)
}

export async function validateYardiResidentTCode(payload: {
  property_id: string | number
  company_id: string | number
  property_code: string
  resident_default_id: string | number
  tCode: string
}): Promise<YardiTCodeValidationResponse> {
  const { data } = await apiClient.post('/api/validate-pms-resident-tcode-mapping', payload)
  return unwrapObject<YardiTCodeValidationResponse>(data)
}

export async function confirmManualYardiResidentMapping(
  context: Omit<YardiSyncContext, 'resident_type'> & { resident_default_id: string | number },
  tCode: string,
  resident: Record<string, unknown>,
): Promise<unknown> {
  const raw =
    resident.soap_json != null
      ? {
          tCode: resident.tCode,
          pCode: resident.pCode,
          status: resident.status,
          firstName: resident.firstName,
          lastName: resident.lastName,
          email: resident.email,
          sourceRole: 'PRIMARY',
          unitCode: resident.unitCode,
          leaseStartDate: resident.leaseFromDate,
          moveInDate: resident.moveInDate,
          soap_json: resident.soap_json,
        }
      : resident
  const selectedMapping: YardiResidentMatch = {
    liberty_entity_type: 'RESIDENT',
    liberty_entity_id: context.resident_default_id,
    liberty_company_id: context.company_id,
    liberty_property_id: context.property_id,
    liberty_property_code: context.property_code,
    liberty_matched_candidate: { resident_default_id: context.resident_default_id },
    liberty_main_candidate: { resident_default_id: context.resident_default_id },
    liberty_ranked_candidates: [],
    source_system: 'YARDI',
    source_entity_type: 'RESIDENT',
    source_entity_id: tCode,
    source_candidate_type: 'PRIMARY',
    source_p_code: resident.pCode ?? resident.propertyCode ?? context.property_code,
    source_main_candidate: resident,
    source_matched_candidate: {
      sourceRole: 'PRIMARY',
      sourceType: 'current',
      sourceEntityId: tCode,
      firstName: resident.firstName == null ? undefined : String(resident.firstName),
      lastName: resident.lastName == null ? undefined : String(resident.lastName),
      email: resident.email == null ? undefined : String(resident.email),
      unitCode: resident.unitCode == null ? undefined : String(resident.unitCode),
      leaseStartDate: resident.leaseFromDate == null ? undefined : String(resident.leaseFromDate),
      moveInDate: resident.moveInDate == null ? undefined : String(resident.moveInDate),
      raw,
    },
    source_ranked_candidates: [],
    yardi_ranked_candidates: [],
    change_reason: 'MANUAL_OVERRIDE',
  }
  const requestContext: Partial<YardiSyncContext> = { ...context }
  delete requestContext.resident_default_id
  const { data } = await apiClient.post('/api/confirm-yardi-resident-mappings', {
    ...requestContext,
    resident_type: 'current',
    selected_mappings: [selectedMapping],
  })
  return unwrapObject<unknown>(data)
}
